import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ToggleButton } from "primereact/togglebutton";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { Divider } from "primereact/divider";
import { useToast } from "../../utils/toast";
import dynamicSurveyService, { DynamicQuestionDto, DynamicSurveyDto } from "../../services/forms/dynamic.service";

const QUESTION_TYPES = [
  { label: "Serbest Metin", value: "text" },
  { label: "Tek Seçmeli", value: "single_choice" },
  { label: "Çok Seçmeli", value: "multiple_choice" },
  { label: "Derecelendirme (1-5)", value: "rating" },
];

const TYPE_LABELS: Record<string, string> = {
  text: "Serbest Metin",
  single_choice: "Tek Seçmeli",
  multiple_choice: "Çok Seçmeli",
  rating: "Derecelendirme (1-5)",
};

const emptyQuestion = (): DynamicQuestionDto => ({
  text: "",
  question_type: "text",
  order: 0,
  choices: [],
});

const DynamikAnketOlustur = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const toast = useToast();

  // Anket bilgileri
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [questions, setQuestions] = useState<DynamicQuestionDto[]>([]);
  const [saving, setSaving] = useState(false);

  // Soru modal state
  const [soruDialogVisible, setSoruDialogVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<DynamicQuestionDto>(emptyQuestion());
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);

  // Edit modunda mevcut anketi yükle
  const { data: surveyData, isLoading: surveyLoading } = useQuery({
    queryKey: ["dynamicSurveyDetail", id],
    queryFn: () => dynamicSurveyService.getSurveyDetail(Number(id)),
    enabled: isEditMode,
  });

  useEffect(() => {
    if (surveyData?.result?.data) {
      const s = surveyData.result.data;
      setTitle(s.title);
      setDescription(s.description ?? "");
      setIsActive(s.is_active);
      setQuestions(s.questions ? s.questions.map((q) => ({ ...q, choices: q.choices ? [...q.choices] : [] })) : []);
    }
  }, [surveyData]);

  // ── Soru modal işlemleri ──────────────────────────────────────────────

  const openAddQuestion = () => {
    setEditingQuestion({ ...emptyQuestion(), order: questions.length });
    setEditingQuestionIndex(null);
    setSoruDialogVisible(true);
  };

  const openEditQuestion = (index: number) => {
    const q = questions[index];
    setEditingQuestion({ ...q, choices: q.choices ? [...q.choices] : [] });
    setEditingQuestionIndex(index);
    setSoruDialogVisible(true);
  };

  const saveQuestion = () => {
    if (!editingQuestion.text.trim()) {
      toast.show("Soru metni boş bırakılamaz.", "error");
      return;
    }
    if (
      (editingQuestion.question_type === "single_choice" || editingQuestion.question_type === "multiple_choice") &&
      (editingQuestion.choices?.filter((c) => c.text.trim()).length ?? 0) < 2
    ) {
      toast.show("Seçenekli sorularda en az 2 dolu seçenek gereklidir.", "error");
      return;
    }

    const cleaned = {
      ...editingQuestion,
      choices:
        editingQuestion.question_type === "text" || editingQuestion.question_type === "rating"
          ? []
          : editingQuestion.choices?.filter((c) => c.text.trim()),
    };

    if (editingQuestionIndex === null) {
      setQuestions((prev) => [...prev, cleaned]);
    } else {
      setQuestions((prev) => prev.map((q, i) => (i === editingQuestionIndex ? cleaned : q)));
    }
    setSoruDialogVisible(false);
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i })));
  };

  // ── Seçenek işlemleri (modal içi) ────────────────────────────────────

  const addChoice = () => {
    setEditingQuestion((prev) => ({ ...prev, choices: [...(prev.choices ?? []), { text: "" }] }));
  };

  const updateChoice = (cIndex: number, value: string) => {
    setEditingQuestion((prev) => {
      const choices = [...(prev.choices ?? [])];
      choices[cIndex] = { ...choices[cIndex], text: value };
      return { ...prev, choices };
    });
  };

  const removeChoice = (cIndex: number) => {
    setEditingQuestion((prev) => ({
      ...prev,
      choices: prev.choices?.filter((_, i) => i !== cIndex),
    }));
  };

  // ── Anket kaydetme ────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!title.trim()) {
      toast.show("Anket başlığı zorunludur.", "error");
      return;
    }
    if (questions.length === 0) {
      toast.show("En az bir soru eklemelisiniz.", "error");
      return;
    }

    const payload: DynamicSurveyDto = {
      title,
      description,
      is_active: isActive,
      questions: questions.map((q, i) => ({ ...q, order: i })),
    };

    setSaving(true);
    try {
      const res = isEditMode
        ? await dynamicSurveyService.updateSurvey(Number(id), payload)
        : await dynamicSurveyService.createSurvey(payload);

      if (res.result && !res.result.error) {
        toast.show(isEditMode ? "Anket güncellendi." : "Anket oluşturuldu.", "success");
        navigate("/admin/dinamik-anket-yonetim");
      } else {
        const errMsg = res.result?.error ?? "Bir hata oluştu.";
        toast.show(typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg), "error");
      }
    } catch {
      toast.show("Bir hata oluştu.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (isEditMode && surveyLoading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-3 md:p-4" style={{ maxWidth: "860px", margin: "0 auto" }}>
      {/* Başlık */}
      <div className="mb-4">
        <Button
          icon="pi pi-arrow-left"
          label="Listeye Dön"
          text severity="secondary"
          className="mb-2 p-0"
          onClick={() => navigate("/admin/dinamik-anket-yonetim")}
        />
        <h2 className="text-900 font-bold text-2xl m-0">
          {isEditMode ? "Anketi Düzenle" : "Yeni Anket Oluştur"}
        </h2>
      </div>

      {/* Anket bilgileri */}
      <Card className="mb-4 shadow-1 border-round-xl">
        <h3 className="text-900 font-semibold text-lg mt-0 mb-3">Anket Bilgileri</h3>

        <div className="flex flex-column gap-3">
          <div>
            <label className="block text-900 font-medium mb-1">
              Başlık <span className="text-red-400">*</span>
            </label>
            <InputText
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full"
              placeholder="Anket başlığını girin"
            />
          </div>

          <div>
            <label className="block text-900 font-medium mb-1">Açıklama</label>
            <InputTextarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full"
              rows={3}
              placeholder="İsteğe bağlı açıklama"
              autoResize
            />
          </div>

          <div className="flex align-items-center gap-3">
            <ToggleButton
              checked={isActive}
              onChange={(e) => setIsActive(e.value)}
              onLabel="Aktif"
              offLabel="Pasif"
              onIcon="pi pi-eye"
              offIcon="pi pi-eye-slash"
            />
            <span className="text-500 text-sm">
              {isActive
                ? "Anket yayında — kullanıcılar görebilir ve doldurabilir."
                : "Anket pasif — kullanıcılara gösterilmez."}
            </span>
          </div>
        </div>
      </Card>

      {/* Sorular bölümü */}
      <div className="flex align-items-center justify-content-between mb-3">
        <div>
          <h3 className="text-900 font-semibold text-lg m-0">
            Sorular
            <span className="ml-2 text-500 font-normal text-base">({questions.length})</span>
          </h3>
        </div>
        <Button label="Soru Ekle" icon="pi pi-plus" onClick={openAddQuestion} />
      </div>

      {questions.length === 0 ? (
        <div
          className="flex flex-column align-items-center justify-content-center border-2 border-dashed border-round-xl p-6"
          style={{ borderColor: "var(--surface-300)" }}
        >
          <i className="pi pi-list text-400" style={{ fontSize: "2rem" }} />
          <p className="text-500 mt-3 mb-2">Henüz soru eklenmedi.</p>
          <Button label="İlk Soruyu Ekle" icon="pi pi-plus" outlined onClick={openAddQuestion} />
        </div>
      ) : (
        <div className="flex flex-column gap-3 mb-4">
          {questions.map((q, index) => (
            <Card
              key={index}
              className="shadow-1 border-round-xl"
              style={{ borderLeft: "4px solid var(--primary-color)" }}
            >
              <div className="flex align-items-start justify-content-between">
                <div className="flex align-items-start gap-3 flex-1">
                  <span
                    className="flex align-items-center justify-content-center border-circle font-bold text-sm flex-shrink-0 text-white"
                    style={{ width: "28px", height: "28px", minWidth: "28px", background: "var(--primary-color)" }}
                  >
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-900 font-medium m-0 mb-1" style={{ lineHeight: "1.5" }}>
                      {q.text}
                    </p>
                    <div className="flex align-items-center gap-2 flex-wrap">
                      <Tag value={TYPE_LABELS[q.question_type]} severity="info" style={{ fontSize: "0.7rem" }} />
                      {(q.question_type === "single_choice" || q.question_type === "multiple_choice") && (
                        <span className="text-500 text-xs">{q.choices?.length ?? 0} seçenek</span>
                      )}
                    </div>
                    {(q.question_type === "single_choice" || q.question_type === "multiple_choice") &&
                      (q.choices?.length ?? 0) > 0 && (
                        <ul className="mt-2 mb-0 pl-4" style={{ listStyleType: "disc" }}>
                          {q.choices?.map((c, ci) => (
                            <li key={ci} className="text-600 text-sm">
                              {c.text}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <Button
                    icon="pi pi-pencil"
                    rounded text severity="warning"
                    tooltip="Düzenle" tooltipOptions={{ position: "top" }}
                    onClick={() => openEditQuestion(index)}
                  />
                  <Button
                    icon="pi pi-trash"
                    rounded text severity="danger"
                    tooltip="Sil" tooltipOptions={{ position: "top" }}
                    onClick={() => removeQuestion(index)}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Kaydet butonu */}
      <Divider />
      <div className="flex justify-content-end gap-2">
        <Button
          label="İptal"
          icon="pi pi-times"
          outlined severity="secondary"
          onClick={() => navigate("/admin/dinamik-anket-yonetim")}
          disabled={saving}
        />
        <Button
          label={isEditMode ? "Güncelle" : "Anketi Oluştur"}
          icon="pi pi-save"
          loading={saving}
          onClick={handleSave}
        />
      </div>

      {/* ── Soru Ekleme / Düzenleme Modali ─────────────────────────────── */}
      <Dialog
        header={editingQuestionIndex === null ? "Yeni Soru Ekle" : "Soruyu Düzenle"}
        visible={soruDialogVisible}
        onHide={() => setSoruDialogVisible(false)}
        style={{ width: "min(520px, 95vw)" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="İptal"
              icon="pi pi-times"
              outlined severity="secondary"
              onClick={() => setSoruDialogVisible(false)}
            />
            <Button
              label={editingQuestionIndex === null ? "Ekle" : "Kaydet"}
              icon={editingQuestionIndex === null ? "pi pi-plus" : "pi pi-check"}
              onClick={saveQuestion}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div>
            <label className="block text-900 font-medium mb-1">
              Soru Metni <span className="text-red-400">*</span>
            </label>
            <InputTextarea
              value={editingQuestion.text}
              onChange={(e) => setEditingQuestion((prev) => ({ ...prev, text: e.target.value }))}
              rows={3}
              className="w-full"
              placeholder="Soru metnini girin..."
              autoResize
            />
          </div>

          <div>
            <label className="block text-900 font-medium mb-1">Soru Tipi</label>
            <Dropdown
              value={editingQuestion.question_type}
              options={QUESTION_TYPES}
              onChange={(e) =>
                setEditingQuestion((prev) => ({
                  ...prev,
                  question_type: e.value,
                  choices: e.value === "text" || e.value === "rating" ? [] : prev.choices,
                }))
              }
              className="w-full"
            />
          </div>

          {(editingQuestion.question_type === "single_choice" ||
            editingQuestion.question_type === "multiple_choice") && (
            <div>
              <div className="flex align-items-center justify-content-between mb-2">
                <label className="text-900 font-medium">
                  Seçenekler <span className="text-red-400">*</span>
                </label>
                <Button label="Seçenek Ekle" icon="pi pi-plus" size="small" text onClick={addChoice} />
              </div>
              <div className="flex flex-column gap-2">
                {(editingQuestion.choices ?? []).length === 0 && (
                  <p className="text-500 text-sm m-0">Henüz seçenek eklenmedi.</p>
                )}
                {(editingQuestion.choices ?? []).map((choice, cIndex) => (
                  <div key={cIndex} className="flex align-items-center gap-2">
                    <InputText
                      value={choice.text}
                      onChange={(e) => updateChoice(cIndex, e.target.value)}
                      placeholder={`Seçenek ${cIndex + 1}`}
                      className="flex-1"
                    />
                    <Button
                      icon="pi pi-times"
                      rounded text severity="danger"
                      onClick={() => removeChoice(cIndex)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {editingQuestion.question_type === "rating" && (
            <div
              className="flex align-items-center gap-2 p-3 border-round"
              style={{ background: "var(--blue-50)", border: "1px solid var(--blue-200)" }}
            >
              <i className="pi pi-star-fill text-yellow-500" />
              <span className="text-700 text-sm">Kullanıcılar 1'den 5'e kadar yıldız ile değerlendirecek.</span>
            </div>
          )}

          {editingQuestion.question_type === "text" && (
            <div
              className="flex align-items-center gap-2 p-3 border-round"
              style={{ background: "var(--surface-50)", border: "1px solid var(--surface-200)" }}
            >
              <i className="pi pi-align-left text-500" />
              <span className="text-500 text-sm">Kullanıcılar serbest metin girecek.</span>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default DynamikAnketOlustur;
